import {
  nextOrderNumberSchema,
  orderItemSchema,
  orderPageSchema,
  orderWithItemsSchema,
  type AdjustItemMeasurementsInput,
  type CreateOrderInput,
  type NextOrderNumber,
  type OrderFilter,
  type OrderItem,
  type OrderPage,
  type OrderSortField,
  type OrderWithItems,
  type UpdateOrderInput,
  type UpdateOrderItemInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every order call the app makes.
 *
 * Note what no request here carries: a total. The subtotal, the total and the balance are the
 * server's to compute from the prices, and a client that sent its own would simply have them
 * ignored — so this module does not offer a way to try.
 */

const BASE = '/orders';

export interface OrderListParams {
  filter?: OrderFilter;
  customerId?: string;
  q?: string;
  sortBy?: OrderSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export async function listOrders(
  client: ApiClient,
  params: OrderListParams = {},
): Promise<OrderPage> {
  return await client.get(BASE, {
    schema: orderPageSchema,
    query: {
      filter: params.filter ?? 'all',
      ...(params.customerId ? { customerId: params.customerId } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortDir ? { sortDir: params.sortDir } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}

export async function getOrder(client: ApiClient, orderId: string): Promise<OrderWithItems> {
  return await client.get(`${BASE}/${orderId}`, { schema: orderWithItemsSchema });
}

/**
 * What the next booking will be numbered — a peek at the counter, not a claim on it. The server
 * says so too; nothing here may cache it as though the number were held.
 */
export async function nextOrderNumber(client: ApiClient): Promise<NextOrderNumber> {
  return await client.get(`${BASE}/next-number`, { schema: nextOrderNumberSchema });
}

export async function createOrder(
  client: ApiClient,
  input: CreateOrderInput,
): Promise<OrderWithItems> {
  return await client.post(BASE, input, { schema: orderWithItemsSchema });
}

export async function updateOrder(
  client: ApiClient,
  orderId: string,
  patch: UpdateOrderInput,
): Promise<OrderWithItems> {
  return await client.patch(`${BASE}/${orderId}`, patch, { schema: orderWithItemsSchema });
}

export async function updateOrderItem(
  client: ApiClient,
  orderId: string,
  itemId: string,
  patch: UpdateOrderItemInput,
): Promise<OrderWithItems> {
  return await client.patch(`${BASE}/${orderId}/items/${itemId}`, patch, {
    schema: orderWithItemsSchema,
  });
}

/** Adjusts this garment's frozen copy. Never the customer's profile — that is the point. */
export async function adjustItemMeasurements(
  client: ApiClient,
  orderId: string,
  itemId: string,
  input: AdjustItemMeasurementsInput,
): Promise<OrderItem> {
  return await client.patch(`${BASE}/${orderId}/items/${itemId}/measurements`, input, {
    schema: orderItemSchema,
  });
}

export async function cancelOrder(
  client: ApiClient,
  orderId: string,
  reason: string,
): Promise<OrderWithItems> {
  return await client.post(
    `${BASE}/${orderId}/cancel`,
    { reason },
    {
      schema: orderWithItemsSchema,
    },
  );
}

export async function cancelOrderItem(
  client: ApiClient,
  orderId: string,
  itemId: string,
  reason: string,
): Promise<OrderWithItems> {
  return await client.post(
    `${BASE}/${orderId}/items/${itemId}/cancel`,
    { reason },
    {
      schema: orderWithItemsSchema,
    },
  );
}

export async function repeatOrder(
  client: ApiClient,
  orderId: string,
  promisedDate?: string | null,
): Promise<OrderWithItems> {
  return await client.post(
    `${BASE}/${orderId}/repeat`,
    { promisedDate: promisedDate ?? null },
    { schema: orderWithItemsSchema },
  );
}
